/**
 * EmptyState.tsx — Primitiva E1 de estado vacío / error.
 *
 * Patrón: CVA + tokens cumple-* (ADR-025 + SH-1/ADR-029).
 * Referencia canónica: Button.tsx.
 *
 * Variantes semánticas:
 *   - empty (default): estado vacío real (sin datos). Borde dashed gray.
 *   - filtered: vacío por filtro activo. Mismo visual que empty + copy diferente (decide el consumidor).
 *   - error: fallo de carga. Borde/fondo danger tintado + role="alert" automático.
 *
 * Props:
 *   - title: string (obligatorio) — texto principal visible.
 *   - description?: string — texto secundario.
 *   - icon?: ReactNode — ícono Lucide decorativo (aria-hidden automático).
 *   - action?: ReactNode — render-prop para CTA; acepta <Button> con cualquier variante.
 *   - size?: 'sm' | 'md' — 'md' es el default para pantallas completas;
 *       'sm' para paneles compactos (ej. SurveyRenderer evidence). R4 ADR-029.
 *   - variant?: 'empty' | 'filtered' | 'error'
 *
 * WCAG:
 *   - variant="error" añade role="alert" (assertive) — anuncia a AT inmediatamente.
 *   - variant="empty"/"filtered" sin role especial (el consumidor puede pasar role="status"
 *     si el estado aparece dinámicamente, ej. tras limpiar filtros).
 *   - icon siempre aria-hidden; el título comunica el estado.
 *   - action (CTA) debe ser Button E1 para garantizar min-h-[44px] táctil.
 *
 * Migración progresiva: el EmptyState legacy de ui.tsx NO se borra (DEUDA-V2-UI ADR-025).
 */
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './utils';

// ---------------------------------------------------------------------------
// Variantes CVA
// ---------------------------------------------------------------------------

const emptyStateVariants = cva(
  // Base: contenedor centrado con borde dashed
  ['rounded-2xl border border-dashed', 'text-center', 'flex flex-col items-center justify-center'],
  {
    variants: {
      variant: {
        /**
         * empty — Estado vacío real. Neutro, no alarmante.
         */
        empty: ['border-cumple-gray-200', 'bg-cumple-gray-50'],
        /**
         * filtered — Vacío causado por filtros activos. Mismo visual que empty.
         * La distinción semántica la aporta el copy, no el color.
         */
        filtered: ['border-cumple-gray-200', 'bg-cumple-gray-50'],
        /**
         * error — Fallo de carga o error de sistema. Tintado danger.
         * role="alert" se aplica en el componente para anuncio assertive a AT.
         */
        error: ['border-cumple-danger-500/20', 'bg-cumple-danger-500/[.05]'],
      },
      size: {
        /** md — Para pantallas completas y tabs. p-10, ícono h-16 w-16. */
        md: 'p-10 space-y-3',
        /** sm — Para paneles compactos (SurveyRenderer evidence). p-6, ícono h-12 w-12. */
        sm: 'p-6 space-y-2',
      },
    },
    defaultVariants: {
      variant: 'empty',
      size: 'md',
    },
  },
);

const iconWrapperVariants = cva(
  ['mx-auto mb-1 flex items-center justify-center rounded-xl bg-white'],
  {
    variants: {
      variant: {
        empty: 'text-cumple-gray-400',
        filtered: 'text-cumple-gray-400',
        error: 'text-cumple-danger-600',
      },
      size: {
        md: 'h-16 w-16',
        sm: 'h-12 w-12',
      },
    },
    defaultVariants: {
      variant: 'empty',
      size: 'md',
    },
  },
);

const titleVariants = cva(['font-semibold'], {
  variants: {
    variant: {
      empty: 'text-cumple-primary-900',
      filtered: 'text-cumple-primary-900',
      error: 'text-cumple-primary-900',
    },
    size: {
      md: 'text-base',
      sm: 'text-sm',
    },
  },
  defaultVariants: {
    variant: 'empty',
    size: 'md',
  },
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EmptyStateProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof emptyStateVariants> {
  /** Texto principal del estado vacío/error. */
  title: string;
  /** Texto descriptivo secundario. */
  description?: string;
  /**
   * Ícono Lucide decorativo. El componente lo envuelve en aria-hidden.
   * Ejemplo: import { ShieldCheck } from 'lucide-react'; → icon={<ShieldCheck />}
   */
  icon?: React.ReactNode;
  /**
   * Acción opcional. Render-prop: acepta cualquier ReactNode (Button E1, Link…).
   * Al ser render-prop, desacopla de react-router y permite tanto callbacks como navegación.
   * Ejemplo: action={<Button variant="secondary" size="md" onClick={clearFilters}>Limpiar filtros</Button>}
   */
  action?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * EmptyState — Estado vacío, filtrado o de error.
 *
 * variant="error" añade automáticamente role="alert" para que AT lo anuncie
 * de forma assertive (WCAG 4.1.3). Para empty/filtered, el consumidor puede
 * pasar role="status" cuando el estado aparece de forma dinámica.
 *
 * El ícono es siempre decorativo (aria-hidden); el título comunica el estado.
 *
 * @example Estado vacío real
 *   <EmptyState
 *     icon={<ShieldCheck />}
 *     title="Sin hallazgos registrados"
 *     description="Esta auditoría no tiene hallazgos."
 *   />
 *
 * @example Vacío por filtro con CTA
 *   <EmptyState
 *     variant="filtered"
 *     icon={<Filter />}
 *     title="Sin hallazgos con estos filtros"
 *     action={<Button variant="secondary" size="md" onClick={clearFilters}>Limpiar filtros</Button>}
 *   />
 *
 * @example Error de carga
 *   <EmptyState
 *     variant="error"
 *     icon={<AlertCircle />}
 *     title="No se pudieron cargar los hallazgos"
 *     description={error}
 *     action={<Button variant="secondary" size="sm" onClick={retry}>Reintentar</Button>}
 *   />
 */
const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    { className, variant = 'empty', size = 'md', title, description, icon, action, ...props },
    ref,
  ) => {
    // variant="error" siempre lleva role="alert" para anuncio assertive a AT.
    // El consumidor puede sobreescribir pasando role explícito en props.
    const roleAttr = variant === 'error' ? 'alert' : undefined;

    return (
      <div
        ref={ref}
        role={roleAttr}
        className={cn(emptyStateVariants({ variant, size }), className)}
        {...props}
      >
        {icon && (
          // Envuelve el ícono en aria-hidden: es decorativo, el título ya comunica el estado.
          <div aria-hidden="true" className={iconWrapperVariants({ variant, size })}>
            {icon}
          </div>
        )}

        <p className={titleVariants({ variant, size })}>{title}</p>

        {description && (
          <p
            className={cn(
              'text-cumple-gray-500 leading-relaxed mx-auto',
              size === 'md' ? 'text-sm max-w-sm' : 'text-xs max-w-xs',
            )}
          >
            {description}
          </p>
        )}

        {action && <div className="mt-2 flex justify-center">{action}</div>}
      </div>
    );
  },
);

EmptyState.displayName = 'EmptyState';

export { EmptyState, emptyStateVariants };
